import React, { useState, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useInvoice } from '../contexts/InvoiceContext';
import { InvoiceStatus, INVOICE_STATUS_CONFIG } from '../../../types/accounting';
import { useIsMobile } from '../../../hooks/useIsMobile';

const ALL_STATUSES: InvoiceStatus[] = ['DRAFT', 'APPROVED', 'PAID', 'VOIDED'];

interface InvoiceFiltersProps {
  onOpenCreditNotes?: () => void;
  embedded?: boolean;
}

export function InvoiceFilters({ onOpenCreditNotes, embedded }: InvoiceFiltersProps) {
  const { invoiceFilters, setInvoiceFilters, fetchInvoices } = useInvoice();
  const statuses = Array.isArray(invoiceFilters.status)
    ? invoiceFilters.status
    : invoiceFilters.status
    ? [invoiceFilters.status]
    : [];

  const [search, setSearch] = useState(invoiceFilters.search || '');
  const [dateFrom, setDateFrom] = useState(invoiceFilters.dateFrom || '');
  const [dateTo, setDateTo] = useState(invoiceFilters.dateTo || '');
  const [showDateRange, setShowDateRange] = useState(!!invoiceFilters.dateFrom || !!invoiceFilters.dateTo);
  const isMobile = useIsMobile(768);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== invoiceFilters.search) {
        setInvoiceFilters({ search });
        fetchInvoices({ search });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, invoiceFilters.search, setInvoiceFilters, fetchInvoices]);

  useEffect(() => {
    setDateFrom(invoiceFilters.dateFrom || '');
    setDateTo(invoiceFilters.dateTo || '');
    if (invoiceFilters.dateFrom || invoiceFilters.dateTo) setShowDateRange(true);
  }, [invoiceFilters.dateFrom, invoiceFilters.dateTo]);

  useEffect(() => {
    // Apply a ONE-sided ("from X onwards" / "up to Y") OR two-sided range —
    // previously both fields were required, so a one-sided range was silently
    // ignored (audit: medium). Empty fields are open-ended (undefined).
    const t = setTimeout(() => {
      const from = dateFrom || undefined;
      const to = dateTo || undefined;
      if (from === (invoiceFilters.dateFrom || undefined) && to === (invoiceFilters.dateTo || undefined)) return;
      setInvoiceFilters({ dateFrom: from, dateTo: to });
      fetchInvoices({ dateFrom: from, dateTo: to });
    }, 300);
    return () => clearTimeout(t);
  }, [dateFrom, dateTo]);

  const handleStatusChange = (status: InvoiceStatus) => {
    let newStatuses: InvoiceStatus[];
    if (statuses.includes(status)) {
      newStatuses = statuses.filter(s => s !== status);
    } else {
      newStatuses = [...statuses, status];
    }
    setInvoiceFilters({ status: newStatuses.length ? newStatuses : undefined });
    fetchInvoices({ status: newStatuses.length ? newStatuses : undefined });
  };

  const getStatusLabel = () => {
    if (statuses.length === 0) return 'All Statuses';
    if (statuses.length === 1) return INVOICE_STATUS_CONFIG[statuses[0]].label;
    return `${statuses.length} selected`;
  };

  const clearDateRange = () => {
    setDateFrom('');
    setDateTo('');
    setShowDateRange(false);
    setInvoiceFilters({ dateFrom: undefined, dateTo: undefined });
    fetchInvoices({ dateFrom: undefined, dateTo: undefined });
  };

  const hasActiveFilters =
    statuses.length > 0 ||
    !!invoiceFilters.search ||
    !!invoiceFilters.dateFrom ||
    !!invoiceFilters.dateTo;
  const hasDateRange = !!invoiceFilters.dateFrom || !!invoiceFilters.dateTo;

  const clearAllFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setShowDateRange(false);
    setInvoiceFilters({ status: undefined, search: undefined, dateFrom: undefined, dateTo: undefined });
    fetchInvoices({ status: undefined, search: undefined, dateFrom: undefined, dateTo: undefined });
  };

  const statusSection = (
    <>
      <div className="dropdown-header text-uppercase fs-8 text-muted mb-2">Status</div>
      <label className="dropdown-item d-flex align-items-center py-3 mb-0 accounting-filter-touch" style={{ cursor: 'pointer' }}>
        <input
          type="checkbox"
          className="form-check-input me-2 mt-0"
          checked={statuses.length === 0}
          onChange={() => {
            setInvoiceFilters({ status: undefined });
            fetchInvoices({ status: undefined });
          }}
        />
        All Statuses
      </label>
      {ALL_STATUSES.map((status) => {
        const config = INVOICE_STATUS_CONFIG[status];
        const isActive = statuses.includes(status);
        return (
          <label key={status} className="dropdown-item d-flex align-items-center py-3 mb-0 accounting-filter-touch" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              className="form-check-input me-2 mt-0"
              checked={isActive}
              onChange={() => handleStatusChange(status)}
            />
            <span className={`badge ${config.badgeClass} me-2`} style={{ width: '8px', height: '8px', padding: 0 }} />
            {config.label}
          </label>
        );
      })}
    </>
  );

  const dateSection = (
    <>
      <div className="dropdown-header text-uppercase fs-8 text-muted mt-3 mb-2">Date range</div>
      <div className="d-flex align-items-center gap-2 flex-wrap px-3 pb-2">
        <input
          type="date"
          className="form-control form-control-sm accounting-input-touch"
          style={{ flex: '1 1 120px', minWidth: 0 }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          title="From date"
        />
        <span className="text-muted small">to</span>
        <input
          type="date"
          className="form-control form-control-sm accounting-input-touch"
          style={{ flex: '1 1 120px', minWidth: 0 }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          title="To date"
        />
        {hasDateRange && (
          <button type="button" className="btn btn-sm btn-light accounting-btn-touch" onClick={clearDateRange}>
            Clear dates
          </button>
        )}
      </div>
    </>
  );

  const btnPad: React.CSSProperties = { padding: '6px 12px', lineHeight: '1.4' };
  const inputPad: React.CSSProperties = { padding: '6px 10px', height: 'auto', lineHeight: '1.4' };

  const filterRowDesktop = (
    <div className="accounting-filters-row d-flex align-items-center gap-3 flex-wrap">
      {/* Search */}
      <div className="d-flex align-items-center position-relative flex-grow-1 accounting-filters-search" style={{ minWidth: '180px', maxWidth: '260px' }}>
        <i className="bi bi-search position-absolute ms-3 text-muted" />
        <input
          type="text"
          className="form-control form-control-sm ps-10"
          style={inputPad}
          placeholder="Search invoices, descriptions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Status dropdown */}
      <div className="dropdown">
        <button
          className="btn btn-sm btn-light dropdown-toggle d-flex align-items-center gap-2"
          style={btnPad}
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <i className="bi bi-filter" />
          {getStatusLabel()}
        </button>
        <ul className="dropdown-menu py-2" style={{ minWidth: '180px' }}>
          <li>
            <label className="dropdown-item d-flex align-items-center py-2 mb-0" style={{ cursor: 'pointer' }}>
              <input type="checkbox" className="form-check-input me-2 mt-0" checked={statuses.length === 0} onChange={() => { setInvoiceFilters({ status: undefined }); fetchInvoices({ status: undefined }); }} />
              All Statuses
            </label>
          </li>
          <li><hr className="dropdown-divider" /></li>
          {ALL_STATUSES.map((status) => {
            const config = INVOICE_STATUS_CONFIG[status];
            const isActive = statuses.includes(status);
            return (
              <li key={status}>
                <label className="dropdown-item d-flex align-items-center py-2 mb-0" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" className="form-check-input me-2 mt-0" checked={isActive} onChange={() => handleStatusChange(status)} />
                  <span className={`badge ${config.badgeClass} me-2`} style={{ width: '8px', height: '8px', padding: 0 }} />
                  {config.label}
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Date range toggle + inputs */}
      {!showDateRange ? (
        <button
          className="btn btn-sm btn-light d-flex align-items-center gap-2"
          style={btnPad}
          onClick={() => setShowDateRange(true)}
          title="Filter by date range"
        >
          <i className="bi bi-calendar3" />
          Date Range
        </button>
      ) : (
        <div className="d-flex align-items-center gap-2">
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ ...inputPad, width: '140px' }}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            title="From date"
          />
          <span className="text-muted small">to</span>
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ ...inputPad, width: '140px' }}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            title="To date"
          />
          <button
            className="btn btn-sm btn-light d-flex align-items-center justify-content-center"
            style={{ ...btnPad, padding: '6px 8px' }}
            onClick={clearDateRange}
            title="Remove date filter"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
      )}

      {/* Clear all (only when filters are active) */}
      {hasActiveFilters && (
        <button
          className="btn btn-sm btn-light-danger d-flex align-items-center gap-2"
          style={btnPad}
          onClick={clearAllFilters}
        >
          <i className="bi bi-x-lg" /> Clear all
        </button>
      )}

      {/* Right-aligned actions */}
      <div className="ms-auto d-flex align-items-center gap-2">
        {onOpenCreditNotes && (
          <button
            type="button"
            className="btn btn-sm btn-light-warning d-flex align-items-center gap-2"
            style={btnPad}
            onClick={onOpenCreditNotes}
          >
            <i className="bi bi-receipt" /> Credit Notes
          </button>
        )}
        <button
          className="btn btn-sm btn-light d-flex align-items-center gap-2"
          style={btnPad}
          onClick={() => fetchInvoices()}
        >
          <i className="bi bi-arrow-clockwise" /> Refresh
        </button>
      </div>
    </div>
  );

  const filterRowMobile = (
    <div className="accounting-filters-row accounting-filters-mobile d-flex align-items-center gap-2">
      <div className="d-flex align-items-center position-relative flex-grow-1 min-w-0">
        <i className="bi bi-search position-absolute ms-3 text-muted" />
        <input
          type="text"
          className="form-control form-control-sm ps-10 accounting-search-mobile"
          placeholder="Search invoices, descriptions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <Dropdown align="end" className="d-flex">
        <Dropdown.Toggle variant="light" size="sm" className="accounting-filter-toggle-mobile d-flex align-items-center gap-2" id="invoice-filters-dropdown">
          <i className="bi bi-sliders" />
          <span className="d-none d-sm-inline">Filters</span>
          {hasActiveFilters && <span className="badge bg-primary rounded-pill" style={{ fontSize: '0.65rem' }} />}
        </Dropdown.Toggle>
        <Dropdown.Menu className="accounting-filter-menu-mobile p-0" style={{ minWidth: 'min(320px, 100vw - 2rem)', maxHeight: '80vh', overflowY: 'auto' }}>
          <div className="p-3">
            {statusSection}
            {dateSection}
            {hasActiveFilters && (
              <button type="button" className="btn btn-sm btn-light w-100 mt-3 accounting-btn-touch" onClick={clearAllFilters}>
                <i className="bi bi-x-lg me-2" /> Clear all filters
              </button>
            )}
            {onOpenCreditNotes && (
              <>
                <div className="dropdown-header text-uppercase fs-8 text-muted mt-3 mb-2">Actions</div>
                <button type="button" className="btn btn-sm btn-light-warning w-100 mb-2 d-flex align-items-center justify-content-center gap-2 accounting-btn-touch" onClick={onOpenCreditNotes}>
                  <i className="bi bi-receipt" /> Credit Notes
                </button>
              </>
            )}
            <button type="button" className="dropdown-item d-flex align-items-center py-3 accounting-filter-touch" onClick={() => fetchInvoices()}>
              <i className="bi bi-arrow-clockwise text-primary me-3" /> Refresh Data
            </button>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  const filterRow = isMobile ? filterRowMobile : filterRowDesktop;

  if (embedded) {
    return <div className="py-3 px-4 px-md-5 border-bottom accounting-filter-embedded-wrap">{filterRow}</div>;
  }
  return (
    <div className="card mb-5">
      <div className="card-body py-3">
        {filterRow}
      </div>
    </div>
  );
}

export default InvoiceFilters;
